import { fuego } from '@store/firebase';
import { CollectionPath } from '@typings/collections.enum';
import pickOpponentPetCandidate from './pickOpponentPet';

import { UserPet } from '@typings/pets/pet.interface';
import { v4 as uuidv4 } from 'uuid';

export type InteractionVariables = {
  myPetName?: string;
  opponentPetName?: string;
  [key: string]: string | undefined;
};

// 템플릿의 ${...} 토큰을 실제 이름으로 대치한다.
// 지원 키: ${나의펫}, ${상대펫}, ${펫이름}(=나의펫 별칭)
export function replaceInteractionPlaceholders(
  template: string,
  vars: InteractionVariables,
): string {
  if (!template || typeof template !== 'string') return '';

  return template.replace(/\$\{([^}]+)\}/g, (_match, rawKey) => {
    const key = String(rawKey).trim();
    switch (key) {
      case '나의펫':
      case '펫이름':
        return vars.myPetName ?? '나의펫';
      case '상대펫':
        return vars.opponentPetName ?? '상대펫';
      default:
        // 알 수 없는 토큰은 원문 유지
        return `\${${rawKey}}`;
    }
  });
}

export default replaceInteractionPlaceholders;

// 가중치 기반 템플릿 선택
export async function genWeightedTemplate() {
  const ref = fuego.db
    .collection(CollectionPath.PETSETTING)
    .doc('interactions');
  const interactionsDoc = await ref.get().then((snap) => snap.data());
  const items = (interactionsDoc as any)?.items || [];
  return genWeightedTemplateFrom(items);
}

// 가중치 선택 로직을 분리하여 대량 처리 시 재사용
export function genWeightedTemplateFrom(
  items: Array<{ text: string; weight: number }> | any[],
): string {
  if (!Array.isArray(items) || items.length === 0) return '';
  const total: number =
    (items as any[])
      .map((it: any) => Number(it?.weight) || 0)
      .reduce((a: number, b: number) => a + b, 0) || 1;
  let r = Math.random() * total;
  for (const it of items) {
    r -= Number(it.weight) || 0;
    if (r <= 0) return it.text || '';
  }
  return items[items.length - 1]?.text || '';
}

export async function getPetName(petID: string): Promise<string> {
  const ref = fuego.db.collection(CollectionPath.PET).doc(petID);
  const snap = await ref.get();
  const pet = snap.data() as UserPet | undefined;
  return pet?.customName || '나의펫';
}

type Props = {
  customMyPetName?: string; // 펫 id가 없을 경우
  petID?: string;
};
export async function generateLogs({ customMyPetName, petID }: Props) {
  const template = await genWeightedTemplate();
  if (!template) {
    alert('상호작용 문구가 없습니다. 먼저 문구를 추가하세요.');
    return;
  }
  const opp = await pickOpponentPetCandidate();
  const oppName = (opp as any)?.customName || '상대펫';
  const myPetName =
    customMyPetName || (petID ? await getPetName(petID) : '나의펫');
  // 모의 출력은 원칙상 내 펫 이름은 고정이지만, 요청에 따라 함께 치환 지원
  const msg = replaceInteractionPlaceholders(template, {
    myPetName,
    opponentPetName: oppName,
  });
  const log = {
    id: `${uuidv4()}_${Date.now()}`,
    message: msg,
    createdAt: new Date(),
  };
  return log;
}

// 대량 처리/배치 최적화를 위한 주입형 API
// - 사전에 불러온 템플릿/상대펫/내 펫 이름을 주입하여 불필요한 네트워크 호출을 방지
export async function generateLogsWithInjected(
  opts: {
    preloadedTemplate?: string;
    preloadedOpponentName?: string;
    preloadedMyPetName?: string;
  } & Props,
) {
  const {
    preloadedTemplate,
    preloadedOpponentName,
    preloadedMyPetName,
    customMyPetName,
    petID,
  } = opts;

  const template = preloadedTemplate || (await genWeightedTemplate());
  if (!template) return;

  const oppName =
    preloadedOpponentName ||
    ((await pickOpponentPetCandidate()) as any)?.customName ||
    '상대펫';

  const myPetName =
    preloadedMyPetName ||
    customMyPetName ||
    (petID ? await getPetName(petID) : '나의펫');

  const msg = replaceInteractionPlaceholders(template, {
    myPetName,
    opponentPetName: oppName,
  });

  return {
    id: `${uuidv4()}_${Date.now()}`,
    message: msg,
    createdAt: new Date(),
  };
}
